import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';

import highchartsInterface, {
    ChartOptionInterface,
    ChartProvider,
    Chart as HighChart,
} from '@veneer/highcharts-interface';

import {Loading} from '@br/idm-components';

import useStyles from './styles';
import {ChartProps} from './types';
import {useChartOptions, useTooltipFormatter} from './utils';

const Chart = (props: ChartProps): JSX.Element => {
    const {minHeight: minHeightProp, type} = props;

    const minHeight = minHeightProp || type === 'donut' ? 300 : 400;

    const {t} = useTranslation(['Chart']);
    const formatter = useTooltipFormatter(type);

    const wrapper = useRef<HTMLDivElement>(null);

    const [graphHeight, setGraphHeight] = useState<number>(0);
    const [graphWidth, setGraphWidth] = useState<number>(0);
    const [ready, setReady] = useState(false);

    const handleResize = useCallback(() => {
        const element = wrapper.current;
        /* istanbul ignore else */
        if (element) {
            setGraphHeight(element.offsetHeight);
            setGraphWidth(element.offsetWidth);
        }
    }, []);

    useEffect(() => {
        /* istanbul ignore else */
        if (document.contains(wrapper.current)) {
            setReady(true);
            handleResize();
        } else {
            const observer = new MutationObserver(() => {
                if (document.contains(wrapper.current)) {
                    setReady(true);
                    handleResize();
                    observer.disconnect();
                }
            });
            observer.observe(document, {attributes: false, childList: true, characterData: false, subtree: true});
            return () => observer.disconnect();
        }

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [handleResize]);

    const baseOptions = {
        tooltip: {
            formatter: formatter,
        },
    };

    const options = {...baseOptions, ...useChartOptions(graphWidth, graphHeight, props)};

    const optionsWithTranslations = {
        ...options,
        lang: {noData: t('Chart:noData', 'No data to display')},
    } as ChartOptionInterface;

    const styles = useStyles(minHeight, graphWidth);

    return (
        <ChartProvider chartInterface={highchartsInterface}>
            <div
                ref={wrapper}
                className={styles.chartWrapper}
            >
                <div className={styles.chartWrapperInner}>
                    {ready ? <HighChart options={optionsWithTranslations} /> : <Loading height={minHeight} />}
                </div>
            </div>
        </ChartProvider>
    );
};

export default Chart;
